import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { SimpleDnsSearchDomain } from "src/api/generated/frontend_internal_v1";
import { ReorderItem, ReorderList } from "src/components/base/reorder-list";
import { Bars2Icon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { Button } from "src/components/base/button";
import Form from "src/components/base/form";
import { ErrorMessage, Field } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { UUID } from "src/api/api_common";
import { ApiInternal } from "src/api/api_internal";
import { isFormError } from "src/utils/form-error";

/**
 * The properties for {@link SearchDomainForm}
 */
export type SearchDomainFormProps = {
    /** The list of search domains */
    domains: Array<SimpleDnsSearchDomain>;

    /**
     * The tenant whose search domains to edit
     *
     * Omit to edit the global domains.
     */
    tenant?: UUID;

    /** Callback after any change */
    onChange: () => void;
};

/**
 * A form for searching for a domain.
 */
export default function SearchDomainForm(props: SearchDomainFormProps) {
    const { domains } = props;

    const [t] = useTranslation("component-search-domain");

    const [orderedList, setOrderedList] = React.useState(domains);

    React.useEffect(() => setOrderedList(domains), [domains]);

    const form = useForm({
        defaultValues: {
            domain: "",
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (domains.find(({ domain }) => domain === value.domain))
                    return {
                        fields: {
                            domain: t("error.domain-already-exists"),
                        },
                    };

                if (props.tenant) {
                    const res = await ApiInternal.tenantsDns.insertSearchDomain(props.tenant, { domain: value.domain });
                    if (isFormError(res)) {
                        return {
                            fields: {
                                domain: res.error.domain_already_exists ? t("error.domain-already-exists") : undefined,
                            },
                        };
                    }
                } else {
                    const res = await ApiInternal.dns.insertSearchDomain({ domain: value.domain });
                    if (isFormError(res)) {
                        return {
                            fields: {
                                domain: res.error.domain_already_exists ? t("error.domain-already-exists") : undefined,
                            },
                        };
                    }
                }

                props.onChange();
                form.reset();
            },
        },
    });

    const commitReorder = async (domain: string) => {
        const old_index = domains.findIndex((entry) => entry.domain === domain);
        const new_index = orderedList.findIndex((entry) => entry.domain === domain);
        if (old_index === -1 || new_index === -1 || old_index === new_index) return;

        if (props.tenant) {
            await ApiInternal.tenantsDns.moveSearchDomain(props.tenant, { old_index, new_index });
        } else {
            await ApiInternal.dns.moveSearchDomain({ old_index, new_index });
        }

        props.onChange();
    };
    const deleteDomain = async (domain: string) => {
        const index = domains.findIndex((entry) => entry.domain === domain);
        if (index === -1) return;

        if (props.tenant) {
            await ApiInternal.tenantsDns.removeSearchDomain(props.tenant, index);
        } else {
            await ApiInternal.dns.removeSearchDomain(index);
        }

        props.onChange();
    };

    return (
        <>
            <ReorderList axis={"y"} values={orderedList} onReorder={setOrderedList}>
                {orderedList.map((entry) => (
                    <ReorderItem
                        key={entry.domain}
                        value={entry}
                        className={"cursor-grab items-center justify-between gap-3 active:cursor-grabbing"}
                        onDragEnd={() => commitReorder(entry.domain)}
                    >
                        <div className={"flex min-w-0 items-center gap-2"}>
                            <Bars2Icon className={"size-4 shrink-0 text-zinc-400 dark:text-zinc-500"} />
                            <span className={"truncate text-sm/5 font-semibold text-zinc-950 dark:text-white"}>
                                {entry.domain}
                            </span>
                        </div>
                        <Button plain={true} title={t("button.delete")} onClick={() => deleteDomain(entry.domain)}>
                            <TrashIcon />
                        </Button>
                    </ReorderItem>
                ))}
            </ReorderList>

            <Form onSubmit={form.handleSubmit}>
                <form.Field name={"domain"}>
                    {(fieldApi) => (
                        <Field>
                            <div
                                className={
                                    "flex flex-col gap-2 rounded-md border border-dashed border-zinc-200 p-2 dark:border-zinc-700"
                                }
                            >
                                <div className={"flex w-full items-center gap-2"}>
                                    <Input
                                        required={true}
                                        maxLength={255}
                                        placeholder={"example.com"}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                    />
                                    <Button type={"submit"} plain={true} title={t("button.add-search-domain")}>
                                        <PlusIcon />
                                    </Button>
                                </div>
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage key={err}>{err}</ErrorMessage>
                                ))}
                            </div>
                        </Field>
                    )}
                </form.Field>
            </Form>
        </>
    );
}
